import {
  Box,
  Typography,
  Stack,
  TablePagination,
  Paper,
  Button,
  Grid,
} from "@mui/material";
import SearchResultCard from "./search-result-card/SearchResultCard";
import SortBy from "./sort-by";
import { useEasierTheme } from "datalign-themes";
import SearchOptions from "datalign-module-template/models/search-options";
import Input from "models/input/input";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FacetFields } from "models/facet-fields";
import FacetList from "../facet-list";
import { useSearchParams } from "react-router-dom";
import InfoOption from "./info-option/InfoOption";
import InfoOptionType from "models/info-option-type";
import Boundary from "models/geospatial/boundary";
import Point from "models/geospatial/point";
import Asset from "datalign-module-template/models/asset";
import SolrService from "services/SolrService";
import SortInput from "components/common/sort/sort";

const solrService = new SolrService();

const SearchResultList = ({
  count,
  inputs,
  searchOptions,
  facetFields,
  HandleFacetsChange: OnFacetsChange,
}: {
  count: number;
  inputs: Input[] | undefined;
  searchOptions: SearchOptions;
  facetFields?: FacetFields;
  HandleFacetsChange: (filterType: string, filter: string) => void;
}) => {
  const { inputService } = solrService;
  const theme = useEasierTheme();

  const [searchParams, setSearchParams] = useSearchParams();
  const [infoOption, setInfoOption] = useState<InfoOptionType>(() => {
    const saved =
      process.env.REACT_APP_STYLING_THEME === "nrdata-orig"
        ? "detail"
        : (localStorage.getItem("infoOption") ?? "summary");
    return saved === "detail" || saved === "summary" ? saved : "summary";
  });
  const [assets, setAssets] = useState<Record<string, Asset[]>>({});
  const [boundaries, setBoundaries] = useState<Record<string, Boundary[]>>({});
  const [points, setPoints] = useState<Record<string, Point[]>>({});
  const [refresh, setRefresh] = useState<boolean>();

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const params = new URLSearchParams(searchParams);
    params.delete("page");
    params.set("rows", event.target.value ?? "10");
    setSearchParams(params);
  };

  const ClearFacets = useCallback(() => {
    const filteredParams = new URLSearchParams();
    for (const key of ["search_term", "order_by", "order_dir"]) {
      const value = searchParams.get(key);
      if (value !== null) {
        filteredParams.set(key, value);
      }
    }
    setSearchParams(filteredParams);
  }, [searchParams]);

  const showData = useMemo(() => {
    const result: Record<string, string[]> = {};
    if (inputs && inputs.length > 0) {
      inputs.forEach((input) => {
        result[input.input_id] = Object.entries(input.metadata)
          .filter(([_, metadata]) => metadata?.card_show)
          .sort(([keyA], [keyB]) => SortInput(keyA, keyB, input))
          .map(([key]) => key);
      });
    }
    return result;
  }, [inputs]);

  useEffect(() => {
    if (!inputs || inputs.length <= 0) return;
    const fetchData = async () => {
      try {
        const getRequests = [];
        const input_ids = inputs.map((input) => input.input_id);

        // Flags to determine if we need to fetch
        let needsBoundaries = false;
        let needsPoints = false;

        for (const input of inputs) {
          const fieldKeys = showData[input.input_id] || [];
          for (const key of fieldKeys) {
            const type = input.metadata[key]?.field_type ?? "";

            if (
              ["form-boundary", "form-bounding-box", "form-map"].includes(type)
            ) {
              needsBoundaries = true;
            }

            if (["form-point", "form-map"].includes(type)) {
              needsPoints = true;
            }

            // No need to continue checking if both are true
            if (needsBoundaries && needsPoints) break;
          }

          if (needsBoundaries && needsPoints) break;
        }

        if (needsBoundaries) {
          getRequests.push(
            inputService.bulk_boundaries(input_ids).then((res) => {
              if (res) setBoundaries(res);
            }),
          );
        }

        if (needsPoints) {
          getRequests.push(
            inputService.bulk_points(input_ids).then((res) => {
              if (res) setPoints(res);
            }),
          );
        }

        getRequests.push(
          inputService.bulk_assets(input_ids).then((res) => {
            if (res) setAssets(res);
          }),
        );
        await Promise.all(getRequests);
      } catch (err) {
        console.error(
          "An error occurred when gathering extra information on inputs",
        );
        console.error(err);
      } finally {
        setRefresh(false);
      }
    };

    fetchData();
  }, [inputs, refresh]);

  return (
    <Paper
      sx={{
        backgroundColor: theme?.Colour.SurfaceContainerLowest() + "aa",
        borderRadius: "0px",
        p: 2,
        mx: { sm: 2, md: 4 },
        alignSelf: "center",
        maxWidth: "95vw",
        width: "100%",
      }}
    >
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignContent="center"
        spacing={1}
      >
        <Grid size={{ xs: 12, md: 9 }}>
          <Stack spacing={2}>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                variant="h2"
                sx={{
                  fontFamily: "inherit",
                  fontWeight: 450,
                  fontSize: "1.626rem",
                  ...(["FAF-nt"].includes(
                    process.env.REACT_APP_STYLING_THEME ?? "",
                  )
                    ? {
                        color: theme?.Colour.Primary(),
                        lineHeight: "36.25px",
                      }
                    : { color: theme?.Colour.OnSurface(), lineHeight: 1.2 }),
                }}
              >
                Search results
              </Typography>
              {!["nrdata-orig"].includes(
                process.env.REACT_APP_STYLING_THEME ?? "",
              ) && (
                <SortBy
                  searchOptions={searchOptions}
                  onOptionsChange={(searchOptions) => {
                    const params = new URLSearchParams(searchParams);
                    params.set(
                      "order_by",
                      searchOptions.order_by?.column_name ?? "updated_at",
                    );
                    params.set(
                      "order_dir",
                      searchOptions.order_by?.order ?? "desc",
                    );
                    setSearchParams(params);
                  }}
                />
              )}
              {!["nrdata-orig"].includes(
                process.env.REACT_APP_STYLING_THEME ?? "",
              ) && (
                <InfoOption
                  infoOption={infoOption}
                  handleInfoOption={(_e, newInfoOption) => {
                    setInfoOption(newInfoOption);
                    localStorage.setItem("infoOption", newInfoOption);
                  }}
                />
              )}
            </Box>
            {!inputs || inputs.length <= 0 ? (
              <Typography p={2} px={4}>
                {!inputs ? "Loading..." : "No results found!"}
              </Typography>
            ) : (
              inputs?.map((input) => (
                <Box key={input.input_id}>
                  <SearchResultCard
                    input={input}
                    infoOption={infoOption}
                    showData={showData[input.input_id] ?? []}
                    assets={assets[input.input_id] ?? []}
                    boundaries={boundaries[input.input_id] ?? []}
                    points={points[input.input_id] ?? []}
                  />
                </Box>
              ))
            )}
            <Paper
              sx={{
                mt: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <TablePagination
                onPageChange={handlePageChange}
                rowsPerPage={searchOptions.page_rows}
                onRowsPerPageChange={handleRowsPerPageChange}
                count={count}
                page={searchOptions.page_num}
                component="div"
              />
              {["nrdata-orig"].includes(
                process.env.REACT_APP_STYLING_THEME ?? "",
              ) && (
                <SortBy
                  searchOptions={searchOptions}
                  onOptionsChange={(searchOptions) => {
                    const params = new URLSearchParams(searchParams);
                    params.set(
                      "order_by",
                      searchOptions.order_by?.column_name ?? "updated_at",
                    );
                    params.set(
                      "order_dir",
                      searchOptions.order_by?.order ?? "desc",
                    );
                    setSearchParams(params);
                  }}
                />
              )}
            </Paper>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Stack spacing={2}>
            <Box display="flex" height={40} alignItems="center">
              <Typography
                variant="h4"
                color={theme?.Colour.OnSurface()}
                sx={{
                  m: "auto auto auto 0",
                  fontFamily: "inherit",
                  fontWeight: 500,
                  lineHeight: 1.2,
                  fontSize: "1.2195rem",
                  ...(["FAF-nt", "nrdata-orig"].includes(
                    process.env.REACT_APP_STYLING_THEME ?? "",
                  )
                    ? {
                        color: "#454347",
                      }
                    : {}),
                }}
              >
                Filters
              </Typography>
              <Button
                size="small"
                onClick={ClearFacets}
                sx={{ bgcolor: theme?.Colour.Secondary() }}
                variant="contained"
              >
                Clear filters
              </Button>
            </Box>
            {facetFields && (
              <FacetList facetFields={facetFields} OnUpdated={OnFacetsChange} />
            )}
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SearchResultList;
